import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { buildTimeline, type StepScript, type TimelineEntry, type Variant } from './types';

/** Milliseconds per step at 1×. Slow enough to read the narration line. */
const BASE_INTERVAL_MS = 1600;

export const SPEEDS = [0.5, 1, 1.5, 2] as const;
export type Speed = (typeof SPEEDS)[number];

export interface PlayerState {
  timeline: TimelineEntry[];
  index: number;
  entry: TimelineEntry | undefined;
  variant: Variant | undefined;
  /** Step counts per variant, for the segmented dot strip above the canvas. */
  variantLengths: number[];
  playing: boolean;
  speed: Speed;
  atStart: boolean;
  atEnd: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  next: () => void;
  prev: () => void;
  restart: () => void;
  seek: (index: number) => void;
  /** Jump to the first step of a variant — used by the variant strip. */
  seekVariant: (variantIndex: number) => void;
  setSpeed: (speed: Speed) => void;
}

/**
 * Transport state machine for a step-script.
 *
 * The timeline is flat across variants on purpose: a problem's brute-force and
 * optimized approaches play as one continuous run so the student feels the
 * improvement instead of choosing between two separate demos.
 */
export function usePlayer(script: StepScript | undefined, options?: { skipAutoRewind?: boolean }): PlayerState {
  const timeline = useMemo(() => (script ? buildTimeline(script) : []), [script]);
  const variantLengths = useMemo(
    () => script?.variants.map((v) => v.steps.length) ?? [],
    [script],
  );

  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1);

  // Navigating to another topic swaps the script under us; rewind rather than
  // stranding the index past the end of a shorter timeline. Skipped in the
  // trainer's editor preview (`options.skipAutoRewind`, set whenever `Player`
  // is given a `focusStep`) — there, `script` gets a new object identity on
  // every keystroke, and this rewind-to-0 would fight the focus-follow effect
  // that's supposed to keep the preview on whatever step the trainer is
  // actually editing.
  useEffect(() => {
    if (options?.skipAutoRewind) return;
    setIndex(0);
    setPlaying(false);
    // Deliberately keyed on `script` alone — this should only fire when the
    // SCRIPT changes, not whenever a caller's `options` object identity
    // changes (which, unmemoized, would be every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script]);

  const lastIndex = Math.max(0, timeline.length - 1);
  const atStart = index <= 0;
  const atEnd = index >= lastIndex;

  const clamp = useCallback((i: number) => Math.min(Math.max(i, 0), lastIndex), [lastIndex]);

  const play = useCallback(() => {
    if (timeline.length === 0) return;
    // Pressing play on the final step replays from the top; otherwise the
    // button would look broken.
    setIndex((i) => (i >= lastIndex ? 0 : i));
    setPlaying(true);
  }, [lastIndex, timeline.length]);

  const pause = useCallback(() => setPlaying(false), []);
  const toggle = useCallback(() => (playing ? pause() : play()), [playing, pause, play]);

  // Manual stepping takes over from playback — continuing to autoplay after the
  // student grabs the controls fights them.
  const next = useCallback(() => {
    setPlaying(false);
    setIndex((i) => clamp(i + 1));
  }, [clamp]);

  const prev = useCallback(() => {
    setPlaying(false);
    setIndex((i) => clamp(i - 1));
  }, [clamp]);

  const restart = useCallback(() => {
    setPlaying(false);
    setIndex(0);
  }, []);

  const seek = useCallback((i: number) => {
    setPlaying(false);
    setIndex(clamp(i));
  }, [clamp]);

  const seekVariant = useCallback((variantIndex: number) => {
    const target = timeline.findIndex((e) => e.variantIndex === variantIndex);
    if (target < 0) return;
    setPlaying(false);
    setIndex(target);
  }, [timeline]);

  // Advance on a timer while playing, stopping on the last step.
  const playingRef = useRef(playing);
  playingRef.current = playing;

  useEffect(() => {
    if (!playing || timeline.length === 0) return;
    const id = window.setInterval(() => {
      setIndex((i) => {
        if (i >= lastIndex) {
          setPlaying(false);
          return lastIndex;
        }
        return i + 1;
      });
    }, BASE_INTERVAL_MS / speed);
    return () => window.clearInterval(id);
  }, [playing, speed, lastIndex, timeline.length]);

  const entry = timeline[Math.min(index, lastIndex)];
  const variant = entry && script ? script.variants[entry.variantIndex] : undefined;

  return {
    timeline,
    index: Math.min(index, lastIndex),
    entry,
    variant,
    variantLengths,
    playing,
    speed,
    atStart,
    atEnd,
    play,
    pause,
    toggle,
    next,
    prev,
    restart,
    seek,
    seekVariant,
    setSpeed,
  };
}
